import { FieldProps } from 'formik'
import React from 'react'
import FormField, { FormFieldProps } from './form-field'
import CreatableSelect from 'react-select/creatable'
import { components } from 'react-select'

interface MultiSelectCreatableFieldOption {
  value: string
  label: string
  [key: string]: any
}

interface MultiSelectCreatableFieldProps extends FormFieldProps, FieldProps {
  options: MultiSelectCreatableFieldOption[]
  inputFieldName?: string
  onInputChange?(value: string): void
  disableDefaultFilter?: boolean
  onCreateOption?: (inputValue: string) => void
  isLoading?: boolean
  selectRef?: React.RefObject<any>
  isMulti?: boolean
}

const TextOption = (props: any) => {
  props.innerProps['data-test'] = `${props.innerProps['data-test']}-${props.data.value}`
  return <components.Option {...props}>{props.data.label}</components.Option>
}

const ClearIndicator = (props: any) => {
  props.innerProps['data-test'] = `${props.innerProps['data-test']}`
  return <components.ClearIndicator {...props} />
}

const addDataTest = (Component: any, innerProps: any) => (props: any) => (
  <Component {...props} innerProps={Object.assign({}, props.innerProps, innerProps)} />
)

/**
 * Multi-select field with ability to create new options
 * Extends MultiSelectField with creatable functionality
 */
const MultiSelectCreatableField: React.FunctionComponent<
  MultiSelectCreatableFieldProps
> = props => {
  const {
    field,
    form,
    options,
    help,
    onCreateOption,
    isLoading,
    selectRef,
    isMulti = true,
    ...rest
  } = props
  const { required, disabled: isDisabled } = rest

  const value = React.useMemo(() => {
    if (isMulti) {
      return (field.value || [])
        .map((v: string | boolean) => options.find(o => o.value === v))
        .filter(Boolean)
    } else {
      // Single select mode
      return options.find(o => o.value === field.value) || null
    }
  }, [options, field.value, isMulti])

  return (
    <FormField
      label={props.label}
      error={form.errors[field.name] as string}
      required={required}
      help={help}
    >
      <CreatableSelect
        ref={selectRef}
        value={value}
        isMulti={isMulti}
        name={field.name}
        options={options}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder={
          isMulti ? 'Choisir ou créer des catégories...' : 'Choisir ou créer une catégorie...'
        }
        id={`multi-select-creatable-${field.name}`}
        components={{
          Option: addDataTest(TextOption, { 'data-test': `multi-select-option-${field.name}` }),
          ClearIndicator: addDataTest(ClearIndicator, {
            'data-test': `multi-select-clear-${field.name}`,
          }),
        }}
        closeMenuOnSelect={!isMulti}
        onInputChange={value => {
          if (props.inputFieldName) {
            form.setFieldValue(props.inputFieldName, value)
          }
          if (props.onInputChange) {
            props.onInputChange(value)
          }
        }}
        filterOption={props.disableDefaultFilter ? () => true : undefined}
        onChange={v => {
          if (isMulti) {
            form.setFieldValue(
              field.name,
              v.map(i => i.value),
            )
          } else {
            form.setFieldValue(field.name, v?.value || '')
          }
        }}
        onCreateOption={onCreateOption}
        classNames={{ control: state => (state.hasValue ? 'is-not-empty' : 'is-empty') }}
        isDisabled={isDisabled}
        isLoading={isLoading}
        formatCreateLabel={inputValue => `Créer "${inputValue}"`}
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          menu: base => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={document.body}
        {...rest}
      />
    </FormField>
  )
}

export default MultiSelectCreatableField
